import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Target, 
  TrendingUp, 
  Award, 
  Leaf,
  BarChart3,
  Clock,
  ArrowRight,
  Zap,
  Activity
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  BarChart, 
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, token, refreshUser } = useAuth();
  const [decisions, setDecisions] = useState([]);
  const [loyaltyProfile, setLoyaltyProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [decisionsRes, loyaltyRes] = await Promise.all([
        axios.get(`${API}/decisions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/loyalty/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setDecisions(decisionsRes.data);
      setLoyaltyProfile(loyaltyRes.data);
      await refreshUser();
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [token, refreshUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getDecisionTypeColor = (type) => {
    switch (type) {
      case "strategique": return "bg-chart-1 text-white";
      case "tactique": return "bg-chart-3 text-white";
      case "operationnelle": return "bg-chart-5 text-white";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-chart-1";
    if (score >= 50) return "text-chart-4";
    return "text-destructive";
  };

  const recentDecisions = decisions.slice(0, 5);
  const avgScore = decisions.length > 0 
    ? Math.round(decisions.reduce((acc, d) => acc + d.overall_sustainability_score, 0) / decisions.length)
    : 0;

  // Calculate ESG data for charts
  const getESGChartData = () => {
    if (decisions.length === 0) return [];
    
    // Group decisions by month
    const monthlyData = {};
    decisions.forEach(d => {
      const date = new Date(d.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('fr-FR', { month: 'short' });
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: monthName, decisions: [], env: 0, soc: 0, gov: 0, count: 0 };
      }
      monthlyData[monthKey].decisions.push(d);
      monthlyData[monthKey].env += d.esg_scores?.environnement || 0;
      monthlyData[monthKey].soc += d.esg_scores?.social || 0;
      monthlyData[monthKey].gov += d.esg_scores?.gouvernance || 0;
      monthlyData[monthKey].count++;
    });

    return Object.values(monthlyData)
      .map(m => ({
        month: m.month,
        environnement: Math.round(m.env / m.count),
        social: Math.round(m.soc / m.count),
        gouvernance: Math.round(m.gov / m.count)
      }))
      .slice(-6);
  };

  const getDecisionTypesData = () => {
    const types = { strategique: 0, tactique: 0, operationnelle: 0 };
    decisions.forEach(d => {
      if (types[d.decision_type] !== undefined) {
        types[d.decision_type]++;
      }
    });
    return [
      { name: 'Stratégique', value: types.strategique, color: '#22c55e' },
      { name: 'Tactique', value: types.tactique, color: '#3b82f6' },
      { name: 'Opérationnelle', value: types.operationnelle, color: '#f59e0b' },
    ].filter(t => t.value > 0);
  };

  const esgChartData = getESGChartData();
  const decisionTypesData = getDecisionTypesData();

  if (loading) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <main className="flex-1 ml-[260px] p-8 bg-background">
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse-soft text-primary text-lg">Chargement...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 ml-[260px] p-8 bg-background" data-testid="dashboard-main">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {t('dashboard.welcome')}, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('dashboard.subtitle')}
            </p>
          </div>
          <Link to="/decision/new">
            <Button className="rounded-md" data-testid="new-decision-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('dashboard.newDecision')}
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="stat-card card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <Badge variant="outline" className="text-xs">Total</Badge>
              </div>
              <div className="stat-value text-foreground" data-testid="total-decisions">
                {decisions.length}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{t('dashboard.totalDecisions')}</p>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-chart-1/10 flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-chart-1" />
                </div>
                <Badge variant="outline" className="text-xs">{t('common.average')}</Badge>
              </div>
              <div className={`stat-value ${getScoreColor(avgScore)}`} data-testid="avg-score">
                {avgScore}%
              </div>
              <p className="text-sm text-muted-foreground mt-1">{t('dashboard.avgScore')}</p>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-chart-3/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-chart-3" />
                </div>
                <Badge variant="outline" className="text-xs">Points</Badge>
              </div>
              <div className="stat-value text-chart-3" data-testid="loyalty-points">
                {loyaltyProfile?.points || 0}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{t('dashboard.loyaltyPoints')}</p>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-chart-4/10 flex items-center justify-center">
                  <Award className="w-6 h-6 text-chart-4" />
                </div>
                <Badge variant="outline" className="text-xs">{t('dashboard.level')}</Badge>
              </div>
              <div className="stat-value text-chart-4" data-testid="user-level">
                {loyaltyProfile?.level || 1}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{loyaltyProfile?.level_name || t('dashboard.beginner')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        {decisions.length > 0 && (
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* ESG Performance Chart */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    {t('dashboard.esgPerformance')}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">{t('dashboard.scoreEvolution')}</p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                    <span className="text-muted-foreground">{t('esg.environment')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                    <span className="text-muted-foreground">{t('esg.social')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                    <span className="text-muted-foreground">{t('esg.governance')}</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={esgChartData}>
                      <defs>
                        <linearGradient id="colorEnvDash" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorSocDash" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorGovDash" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="month" className="text-muted-foreground" fontSize={12} />
                      <YAxis className="text-muted-foreground" fontSize={12} domain={[0, 100]} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Area type="monotone" dataKey="environnement" stroke="#22c55e" fill="url(#colorEnvDash)" strokeWidth={2} />
                      <Area type="monotone" dataKey="social" stroke="#3b82f6" fill="url(#colorSocDash)" strokeWidth={2} />
                      <Area type="monotone" dataKey="gouvernance" stroke="#f59e0b" fill="url(#colorGovDash)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Decision Types Pie */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle style={{ fontFamily: 'Manrope' }}>{t('dashboard.decisionTypes')}</CardTitle>
                <p className="text-sm text-muted-foreground">{t('dashboard.distribution')}</p>
              </CardHeader>
              <CardContent>
                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={decisionTypesData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {decisionTypesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-2">
                  {decisionTypesData.map((type, index) => (
                    <div key={index} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: type.color }}></div>
                      <span className="text-muted-foreground">{type.name}</span>
                      <span className="font-medium text-foreground">({type.value})</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Level Progress */}
        {loyaltyProfile && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                    {t('dashboard.progressToNext')} {loyaltyProfile.level + 1}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {loyaltyProfile.points_to_next_level} {t('dashboard.pointsRemaining')}
                  </p>
                </div>
                <div className="flex gap-2">
                  {loyaltyProfile.badges?.map((badge) => (
                    <div
                      key={badge.id}
                      className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center"
                      title={badge.name}
                    >
                      <Award className="w-4 h-4 text-primary" />
                    </div>
                  ))}
                </div>
              </div>
              <Progress value={loyaltyProfile.progress_to_next_level} className="h-2" />
            </CardContent>
          </Card>
        )}

        {/* Recent Decisions */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Manrope' }}>{t('dashboard.recentDecisions')}</CardTitle>
                <Link to="/decision/new" className="text-sm text-primary hover:underline">
                  {t('dashboard.viewAll')}
                </Link>
              </CardHeader>
              <CardContent className="space-y-4">
                {recentDecisions.length === 0 ? (
                  <div className="text-center py-12">
                    <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-medium text-foreground mb-2">{t('dashboard.noDecision')}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('dashboard.startAnalyzing')}
                    </p>
                    <Link to="/decision/new">
                      <Button className="rounded-full" data-testid="empty-new-decision-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('dashboard.newDecision')}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  recentDecisions.map((decision) => (
                    <Link
                      key={decision.id}
                      to={`/decision/${decision.id}`}
                      className={`decision-card ${decision.decision_type} block p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors`}
                      data-testid={`decision-card-${decision.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={getDecisionTypeColor(decision.decision_type)}>
                              {decision.decision_type}
                            </Badge>
                            <span className="text-xs text-muted-foreground flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {new Date(decision.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <h4 className="font-medium text-foreground mb-1">{decision.title}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {decision.description}
                          </p>
                        </div>
                        <div className="ml-4 text-right">
                          <div className={`text-2xl font-bold ${getScoreColor(decision.overall_sustainability_score)}`}>
                            {decision.overall_sustainability_score}%
                          </div>
                          <span className="text-xs text-muted-foreground">Score ESG</span>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions & ESG Breakdown */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>{t('dashboard.quickActions')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/decision/new" className="block">
                  <Button variant="outline" className="w-full justify-start rounded-lg">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('dashboard.newDecision')}
                  </Button>
                </Link>
                <Link to="/profile" className="block">
                  <Button variant="outline" className="w-full justify-start rounded-lg">
                    <Award className="w-4 h-4 mr-2" />
                    {t('dashboard.viewBadges')}
                  </Button>
                </Link>
                {user?.role === "admin" && (
                  <Link to="/admin" className="block">
                    <Button variant="outline" className="w-full justify-start rounded-lg">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      {t('nav.admin')}
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>

            {decisions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>{t('dashboard.distribution')} ESG</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { key: "environnement", label: t('esg.environment') },
                    { key: "social", label: t('esg.social') },
                    { key: "gouvernance", label: t('esg.governance') }
                  ].map((category) => {
                    const avgCat = Math.round(
                      decisions.reduce((acc, d) => acc + (d.esg_scores?.[category.key] || 0), 0) / decisions.length
                    );
                    return (
                      <div key={category.key} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-foreground">{category.label}</span>
                          <span className={getScoreColor(avgCat)}>{avgCat}%</span>
                        </div>
                        <Progress value={avgCat} className="h-2" />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
